import {gql} from '@apollo/client'
import {useCallback} from 'react'
import {useDebouncedCallback} from 'use-debounce'
import {ActionFlowState} from '../state/slice/flow/state'
import {
  SaveConfigMutationOptions,
  useSaveConfigMutation
} from './generated/save-config'

const flowToVariables = (flow: ActionFlowState) => ({
  id: flow.id,
  config: flow.config,
  environmentId: flow.environmentId,
  name: flow.name ? flow.name : null
})

export const useSaveConfig = () => {
  const [doMutation, {loading, error}] = useSaveConfigMutation({
    // connector runtime is derived from config and created via trigger, so
    // refetch after saving config to avoid stale UI
    // this will cause a lot of extra queries, so we should look for a better
    // solution. Ultimately I think the best solution will be to keep
    // connector runtime details in the reducer, and to generate the secret
    // client-side
    refetchQueries: ['GetConnectorRuntime']
  })

  const save = useCallback(
    (flow: ActionFlowState, options?: SaveConfigMutationOptions) =>
      doMutation({
        ...options,
        variables: flowToVariables(flow),
        onError: er => {
          // Handling this here will prevent the error from being thrown
          // we'll be able to handle it in the UI instead via the error prop
          console.error('Caught error saving action flow config', er)
        }
      }),
    [doMutation]
  )

  const saveDebounced = useDebouncedCallback(save, 500)

  return {save, saveDebounced, loading, error}
}

gql`
  mutation SaveConfig(
    $id: String
    $config: JSON!
    $environmentId: String!
    $name: String
  ) {
    saveActionFlowConfiguration(
      input: {
        id: $id
        config: $config
        environmentId: $environmentId
        name: $name
      }
    ) {
      actionFlowConfig {
        id
        hasDirtyDraft
        status
        hasPublishedVersions
        currentVersion {
          nodeId
          status
          updated
        }
        versions: actionFlowConfigVersionsByEnvironmentIdAndFlowConfigId {
          nodes {
            nodeId
          }
        }
      }
    }
  }
`
